import React, { useState } from 'react';
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HandThumbDownIcon as HandThumbDownSolidIcon,
  StarIcon as StarSolidIcon,
} from '@heroicons/react/24/solid';
import { OCRFeedback } from '../types/invoice';
import toast from 'react-hot-toast';

interface FeedbackWidgetProps {
  filename: string;
  onFeedbackSubmit: (feedback: OCRFeedback) => void;
  initialFeedback?: OCRFeedback;
  className?: string;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  filename,
  onFeedbackSubmit,
  initialFeedback,
  className = '',
}) => {
  const [rating, setRating] = useState<'good' | 'bad' | null>(initialFeedback?.rating || null);
  const [comment, setComment] = useState(initialFeedback?.comment || '');
  const [accuracyScore, setAccuracyScore] = useState(initialFeedback?.accuracyScore || 0);
  const [selectedIssues, setSelectedIssues] = useState<string[]>(initialFeedback?.issues || []);
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const commonIssues = [
    'Incorrect company name',
    'Wrong invoice number',
    'Incorrect total amount',
    'Missing line items',
    'Wrong dates',
    'Incorrect addresses',
    'Missing vendor information',
    'Currency not detected',
    'Tax calculation errors',
    'Poor text recognition'
  ];

  const handleRatingChange = (newRating: 'good' | 'bad') => {
    setRating(newRating);
    if (newRating === 'good') {
      setAccuracyScore(5); // Default to high score for good rating
      setSelectedIssues([]); // Clear issues for good rating
    } else {
      setAccuracyScore(2); // Default to lower score for bad rating
      setShowDetailedFeedback(true); // Show detailed feedback for bad rating
    }
  };

  const handleIssueToggle = (issue: string) => {
    setSelectedIssues(prev => 
      prev.includes(issue) 
        ? prev.filter(i => i !== issue)
        : [...prev, issue]
    );
  };

  const handleSubmit = async () => {
    if (!rating) {
      toast.error('Please select a rating before submitting');
      return;
    }

    setIsSubmitting(true);
    
    const feedback: OCRFeedback = {
      rating,
      comment: comment.trim() || undefined,
      accuracyScore,
      issues: selectedIssues.length > 0 ? selectedIssues : undefined,
      timestamp: new Date().toISOString(),
    };

    try {
      await onFeedbackSubmit(feedback);
      toast.success('Feedback submitted successfully! Thank you for helping improve our OCR system.');
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = rating !== (initialFeedback?.rating || null) ||
                    comment !== (initialFeedback?.comment || '') ||
                    accuracyScore !== (initialFeedback?.accuracyScore || 0) ||
                    JSON.stringify(selectedIssues) !== JSON.stringify(initialFeedback?.issues || []);

  return (
    <div className={`glass rounded-2xl p-6 border-2 border-dashed border-purple-200 dark:border-purple-700 ${className}`}>
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mr-3">
          <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 dark:text-white">Rate OCR Quality</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            How accurate was the extraction for <span className="font-medium">{filename}</span>?
          </p>
        </div>
      </div>

      {/* Main Rating Buttons */}
      <div className="flex items-center justify-center space-x-6 mb-6">
        <button
          onClick={() => handleRatingChange('good')}
          className={`flex flex-col items-center p-4 rounded-2xl transition-all duration-200 ${
            rating === 'good'
              ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 shadow-lg scale-105'
              : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
        >
          {rating === 'good' ? (
            <HandThumbUpSolidIcon className="h-8 w-8 mb-2" />
          ) : (
            <HandThumbUpIcon className="h-8 w-8 mb-2" />
          )}
          <span className="font-semibold">Good</span>
          <span className="text-xs mt-1">Accurate extraction</span>
        </button>

        <button
          onClick={() => handleRatingChange('bad')}
          className={`flex flex-col items-center p-4 rounded-2xl transition-all duration-200 ${
            rating === 'bad'
              ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 shadow-lg scale-105'
              : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          }`}
        >
          {rating === 'bad' ? (
            <HandThumbDownSolidIcon className="h-8 w-8 mb-2" />
          ) : (
            <HandThumbDownIcon className="h-8 w-8 mb-2" />
          )}
          <span className="font-semibold">Bad</span>
          <span className="text-xs mt-1">Needs improvement</span>
        </button>
      </div>

      {/* Detailed Feedback */}
      {rating && (
        <div className="space-y-4">
          {/* Accuracy Score */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Accuracy Score (1-5 stars)
            </label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setAccuracyScore(star)}
                  className={`p-1 rounded transition-colors ${
                    star <= accuracyScore
                      ? 'text-yellow-500'
                      : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400'
                  }`}
                >
                  {star <= accuracyScore ? (
                    <StarSolidIcon className="h-6 w-6" />
                  ) : (
                    <StarIcon className="h-6 w-6" />
                  )}
                </button>
              ))}
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                ({accuracyScore}/5)
              </span>
            </div>
          </div>

          {/* Issues (for bad ratings) */}
          {rating === 'bad' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                What issues did you find? (Select all that apply)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {commonIssues.map((issue) => (
                  <button
                    key={issue}
                    onClick={() => handleIssueToggle(issue)}
                    className={`text-left p-3 rounded-lg border transition-all duration-200 ${
                      selectedIssues.includes(issue)
                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-200 dark:hover:border-red-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{issue}</span>
                      {selectedIssues.includes(issue) && (
                        <CheckCircleIcon className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Comments (Optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share specific feedback about the extraction accuracy, what worked well, or what needs improvement..."
              rows={3}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Submit Button */}
          {hasChanges && (
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => {
                  setRating(initialFeedback?.rating || null);
                  setComment(initialFeedback?.comment || '');
                  setAccuracyScore(initialFeedback?.accuracyScore || 0);
                  setSelectedIssues(initialFeedback?.issues || []);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {isSubmitting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>{isSubmitting ? 'Submitting...' : 'Submit Feedback'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Already Submitted Feedback */}
      {initialFeedback && !hasChanges && (
        <div className="mt-4 p-4 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-lg">
          <div className="flex items-center mb-2">
            <CheckCircleIcon className="h-5 w-5 text-brand-500 mr-2" />
            <span className="font-medium text-brand-700 dark:text-brand-400">
              Feedback submitted on {new Date(initialFeedback.timestamp || '').toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-brand-600 dark:text-brand-300">
            Thank you for helping improve our OCR system! You can update your feedback above if needed.
          </p>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
